import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  RefreshCw,
  AlertCircle,
  User,
  X,
  Save,
  Upload,
  Image as ImageIcon,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { CommitteeMember } from '@/types';
import ImageUpload from '@/components/ImageUpload';

interface CommitteeFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  whatsapp_number: string;
  committee_position: string;
  committee_description: string;
  profile_picture: string;
  block_number: string;
  flat_number: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  committee_position?: string;
  general?: string;
}

const COMMITTEE_ROLES = [
  'President',
  'Vice President', 
  'Gen.Secretary',
  'Treasurer',
  'Secretary',
  'M.C. Members'
];

const getBaseUrl = () => {
  const envBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_PUBLIC_URL || window.location.origin;
  return envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;
};

const getProfileImageUrl = (url?: string) => {
  if (!url) return '';

  const cleaned = url.trim();
  if (!cleaned) return '';

  if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
    try {
      const parsed = new URL(cleaned);
      const isProfileApiPath = parsed.pathname.startsWith('/api/v1/image-upload/public-file/profile/');
      const isUploadsPath = parsed.pathname.startsWith('/uploads/images/');

      // Keep profile images same-origin to avoid cross-origin policy blocks on some hosts.
      if ((isProfileApiPath || isUploadsPath) && parsed.origin !== window.location.origin) {
        return encodeURI(`${window.location.origin}${parsed.pathname}`);
      }
    } catch (error) {
      // Fall back to original URL if parsing fails.
    }
    return encodeURI(cleaned);
  }

  const baseUrl = getBaseUrl();
  if (cleaned.startsWith('/')) {
    return encodeURI(`${baseUrl}${cleaned}`);
  }

  return encodeURI(`${baseUrl}/${cleaned}`);
};

const CommitteeMembersPage = () => {
  const { user } = useAuth();
  const [committeeMembers, setCommitteeMembers] = useState<CommitteeMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<CommitteeMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingMember, setEditingMember] = useState<CommitteeMember | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CommitteeFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    committee_position: '',
    committee_description: '',
    profile_picture: '',
    block_number: '',
    flat_number: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Check if user is admin
  const isAdmin = user?.role === 'universal_admin' || user?.role === 'office_admin';

  useEffect(() => {
    if (isAdmin) {
      fetchCommitteeMembers();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterMembers();
  }, [committeeMembers, searchTerm, positionFilter]);

  const fetchCommitteeMembers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/committee', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to show only committee members (not all users)
        const committeeData = data.data || [];
        // Sort members by hierarchy
        const sortedMembers = committeeData.sort((a: CommitteeMember, b: CommitteeMember) => {
          const getPositionOrder = (position: string) => {
            const pos = position.toLowerCase();
            if (pos.includes('president')) return 1;
            if (pos.includes('vice president')) return 2;
            if (pos.includes('general secretary') || pos.includes('gen.secretary')) return 3;
            if (pos.includes('treasurer')) return 4;
            if (pos.includes('member') || pos.includes('m.c. members')) return 5;
            return 6;
          };
          
          const orderA = getPositionOrder(a.position);
          const orderB = getPositionOrder(b.position);
          
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          
          // If same position, sort by name
          return a.name.localeCompare(b.name);
        });
        setCommitteeMembers(sortedMembers);
      } else {
        console.error('Failed to fetch committee members');
        setCommitteeMembers([]);
      }
    } catch (error) {
      console.error('Error fetching committee members:', error);
      setCommitteeMembers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterMembers = () => {
    let filtered = committeeMembers;

    if (searchTerm) {
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phone.includes(searchTerm)
      );
    }

    if (positionFilter !== 'all') {
      filtered = filtered.filter(member =>
        member.position === positionFilter
      );
    }

    setFilteredMembers(filtered);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Valid email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.committee_position) newErrors.committee_position = 'Committee position is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof CommitteeFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleImageUploaded = (imageUrl: string) => {
    setFormData(prev => ({
      ...prev,
      profile_picture: imageUrl
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (modalMode === 'create') {
        // Create new user first, then add to committee
        const userResponse = await fetch('/api/v1/users', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formData.phone,
            whatsapp_number: formData.whatsapp_number,
            role: 'member',
            status: 'active',
            password: 'temp123456', // Temporary password
            // member_id will be auto-generated by backend in sequential format (NFCR0001, NFCR0002, etc.)
            block_number: formData.block_number,
            flat_number: formData.flat_number,
            profile_picture: formData.profile_picture
          })
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          
          // Now add to committee
          const committeeResponse = await fetch('/api/v1/committee', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              user_id: userData.data.id,
              position: formData.committee_position,
              description: formData.committee_description
            })
          });

          if (committeeResponse.ok) {
            fetchCommitteeMembers(); // Refresh the list
            closeModal();
          } else {
            const errorData = await committeeResponse.json();
            setErrors({ general: errorData.message || 'Failed to add to committee' });
          }
        } else {
          const errorData = await userResponse.json();
          setErrors({ general: errorData.message || 'Failed to create user' });
        }
      } else {
        // Update existing committee member (including profile picture)
        const response = await fetch(`/api/v1/committee/${editingMember?.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            position: formData.committee_position,
            description: formData.committee_description,
            profile_picture: formData.profile_picture
          })
        });

        if (response.ok) {
          fetchCommitteeMembers(); // Refresh the list
          closeModal();
        } else {
          const errorData = await response.json();
          setErrors({ general: errorData.message || 'Failed to update committee member' });
        }
      }
    } catch (error) {
      console.error('Error saving committee member:', error);
      setErrors({ general: 'An error occurred while saving' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (member: CommitteeMember) => {
    setEditingMember(member);
    setFormData({
      first_name: member.first_name,
      last_name: member.last_name,
      email: member.email,
      phone: member.phone,
      whatsapp_number: member.whatsapp_number || '',
      committee_position: member.position,
      committee_description: member.description || '',
      profile_picture: member.profile_picture || '',
      block_number: member.block_number || '',
      flat_number: member.flat_number || ''
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this member from the committee?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/committee/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });

      if (response.ok) {
        fetchCommitteeMembers(); // Refresh the list
      } else {
        alert('Failed to remove committee member');
      }
    } catch (error) {
      console.error('Error removing committee member:', error);
      alert('An error occurred while removing');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      whatsapp_number: '',
      committee_position: '',
      committee_description: '',
      profile_picture: '',
      block_number: '',
      flat_number: ''
    });
    setErrors({});
  };

  const openCreateModal = () => {
    setModalMode('create');
    setShowModal(true);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 sm:py-6">
            <div className="flex items-center">
              <User className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Committee Members</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Manage RWA committee members (President, Vice President, Secretary, etc.)</p>
              </div>
            </div>
            <button
              onClick={openCreateModal}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Committee Member
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search committee members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Positions</option>
                {COMMITTEE_ROLES.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <button
              onClick={fetchCommitteeMembers}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Committee Members Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading committee members...</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-8 text-center">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No committee members found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || positionFilter !== 'all' 
                  ? 'Try adjusting your search criteria.' 
                  : 'Add committee members like President, Vice President, Secretary, etc.'
                }
              </p>
              {!searchTerm && positionFilter === 'all' && (
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Committee Member
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            {member.profile_picture && (
                              <img
                                className="h-12 w-12 rounded-full object-cover"
                                src={getProfileImageUrl(member.profile_picture)}
                                alt={member.name}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                                  if (fallback) {
                                    fallback.style.display = 'flex';
                                  }
                                }}
                              />
                            )}
                            <div
                              className="h-12 w-12 rounded-full bg-gray-200 items-center justify-center"
                              style={{ display: member.profile_picture ? 'none' : 'flex' }}
                            >
                              <User className="h-6 w-6 text-gray-400" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {member.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {member.position}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <Phone className="w-3 h-3 mr-1 text-gray-400" />
                          {member.phone}
                        </div>
                        {member.whatsapp_number && (
                          <div className="text-gray-500 text-xs">
                            WhatsApp: {member.whatsapp_number}
                          </div>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {member.block_number && member.flat_number ? (
                          <div className="flex items-center">
                            <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                            Block {member.block_number}, Flat {member.flat_number}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Not specified</span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(member)}
                            className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(member.id)}
                            className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50"
                            title="Remove from Committee"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-10 mx-auto p-4 sm:p-0 w-full max-w-2xl">
            <div className="relative bg-white rounded-lg shadow-xl">
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {modalMode === 'create' ? 'Add Committee Member' : 'Edit Committee Member'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                {errors.general && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{errors.general}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.first_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter first name"
                      disabled={modalMode === 'edit'}
                    />
                    {errors.first_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.last_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter last name"
                      disabled={modalMode === 'edit'}
                    />
                    {errors.last_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter email address"
                      disabled={modalMode === 'edit'}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.phone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter phone number"
                      disabled={modalMode === 'edit'}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WhatsApp Number
                    </label>
                    <input
                      type="tel"
                      value={formData.whatsapp_number}
                      onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter WhatsApp number"
                      disabled={modalMode === 'edit'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Committee Position *
                    </label>
                    <select
                      value={formData.committee_position}
                      onChange={(e) => handleInputChange('committee_position', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.committee_position ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select position</option>
                      {COMMITTEE_ROLES.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                    {errors.committee_position && (
                      <p className="mt-1 text-sm text-red-600">{errors.committee_position}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Block Number
                    </label>
                    <input
                      type="text"
                      value={formData.block_number}
                      onChange={(e) => handleInputChange('block_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter block number"
                      disabled={modalMode === 'edit'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Flat Number
                    </label>
                    <input
                      type="text"
                      value={formData.flat_number}
                      onChange={(e) => handleInputChange('flat_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter flat number"
                      disabled={modalMode === 'edit'}
                    />
                  </div>
                </div>

                <div className="mt-4 sm:mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description/Bio
                  </label>
                  <textarea
                    value={formData.committee_description}
                    onChange={(e) => handleInputChange('committee_description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter committee member description or bio"
                  />
                </div>

                <div className="mt-4 sm:mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Profile Picture
                  </label>
                  <ImageUpload
                    category="profile"
                    onImageUploaded={handleImageUploaded}
                    currentImageUrl={formData.profile_picture}
                    disabled={isSubmitting}
                    className="max-w-xs"
                  />
                </div>

                <div className="mt-6 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        {modalMode === 'create' ? 'Creating...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {modalMode === 'create' ? 'Add to Committee' : 'Update Member'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommitteeMembersPage;
